import React from "react"
import {
	parseRadarVersions,
	formatSoftwareVersion,
	formatLastSeen,
	formatDataSize,
} from "../../utils/versionUtils"
import {ConnectionStatusBadge, FwStatusBadge, UploadStatusBadge} from "../StatusBadge"
import {DeviceSymbol} from "../DeviceSymbol"
import {TableRowData} from "../DeviceTableToolbar"

interface DeviceTableRowProps {
	row: TableRowData
	isExpanded: boolean
	isSelected: boolean
	isFavorite: boolean
	activeTab: "classic" | "radar"
	onToggleExpand: () => void
	onToggleSelect: () => void
	onSetActiveTab: (tab: "classic" | "radar") => void
	onRefresh: () => void
	expandedContent: React.ReactNode
}

export const DeviceTableRow = ({
	row,
	isExpanded,
	isSelected,
	isFavorite,
	activeTab,
	onToggleExpand,
	onToggleSelect,
	onSetActiveTab,
	onRefresh,
	expandedContent,
}: DeviceTableRowProps) => {
	const radarVersions = parseRadarVersions(row.radarFirmwareVersions, row.radarFirmwareStatus)

	return (
		<React.Fragment>
			<tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
				<td className="px-6 py-4 whitespace-nowrap text-center">
					<input
						type="checkbox"
						checked={isSelected}
						onChange={onToggleSelect}
						className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
					/>
				</td>
				<td className="px-6 py-4 whitespace-nowrap text-center">
					<DeviceSymbol softwareVersion={row.softwareVersion} />
				</td>
				<td className="px-6 py-4 whitespace-nowrap">
					<div className="flex items-center">
						<button
							onClick={onToggleExpand}
							className="mr-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
							<svg
								className={`w-4 h-4 transform transition-transform ${isExpanded ? "rotate-90" : ""}`}
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 5l7 7-7 7"
								/>
							</svg>
						</button>
						<div className="flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-gray-100">
							{isFavorite && <span className="text-yellow-400 text-xs">⭐</span>}
							{row.macAddress}
						</div>
					</div>
				</td>
				<td className="px-6 py-4 whitespace-nowrap">
					<ConnectionStatusBadge status={row.connectionStatus} />
				</td>
				<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
					{formatLastSeen(row.lastSeen)}
				</td>
				<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
					{formatDataSize(row.dataSize)}
				</td>
				<td className="px-6 py-4 text-sm">
					<div className="space-y-1">
						<div className="text-gray-500 dark:text-gray-400">
							{formatSoftwareVersion(row.softwareVersion) || "N/A"}
						</div>
						{row.fwStatus !== "N/A" && <FwStatusBadge status={row.fwStatus} />}
					</div>
				</td>
				<td className="px-6 py-4 text-sm">
					<div className="space-y-1">
						{radarVersions.length > 0 ? (
							radarVersions.map((version, idx) => (
								<div
									key={idx}
									className={`${
										version.isLatest
											? "text-green-600 dark:text-green-400"
											: "text-orange-500 dark:text-orange-400"
									} font-semibold text-xs`}>
									{version.text}
								</div>
							))
						) : (
							<span className="text-gray-500 dark:text-gray-400">N/A</span>
						)}
						{row.radarFwStatus !== "N/A" && (
							<div
								className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs"
								title={row.radarFwStatus}>
								{row.radarFwStatus}
							</div>
						)}
					</div>
				</td>
				<td className="px-6 py-4 whitespace-nowrap text-sm">
					<UploadStatusBadge status={row.uploadStatus} error={row.uploadError} />
				</td>
				<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
					<button
						onClick={onRefresh}
						className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300">
						Refresh
					</button>
				</td>
			</tr>
			{isExpanded && (
				<tr>
					<td colSpan={10} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
						{expandedContent}
					</td>
				</tr>
			)}
		</React.Fragment>
	)
}
